import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HERO_SLIDES as FALLBACK_HERO_SLIDES } from '../mock';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSlider = ({ slides: initialSlides }) => {
  const slides = initialSlides || FALLBACK_HERO_SLIDES;
  const [i, setI] = useState(0);

  const n = slides.length;

  useEffect(() => {
    if (n <= 1) return;
    const id = setInterval(() => setI(x => (x + 1) % n), 6000);
    return () => clearInterval(id);
  }, [n]);

  // -mt-20 (mobile only) pulls the hero up under the sticky transparent
  // header so the header's `bg-transparent` state actually reveals the hero
  // photo instead of blank page background above it. lg:mt-0 undoes this on
  // desktop, where the header is always solid white.
  return (
    <section className="relative lg:sticky lg:top-20 z-0 -mt-20 lg:mt-0 w-full min-h-[420px] lg:h-[86vh] hero-vh-lg-86 overflow-hidden bg-[#FFFFFF]">
      {slides.map((s, idx) => (
        <div
          key={s.id || idx}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${idx === i ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 lg:pb-24 text-center px-6">
            {s.tagline && <div className="text-white/90 text-[11px] tracking-widest-2 uppercase mb-4">{s.tagline}</div>}
            <h1 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl font-light max-w-3xl leading-tight">{s.title}</h1>
            {s.cta && (
              s.href ? (
                <Link to={s.href} className="btn-cta mt-8">{s.cta}</Link>
              ) : (
                <span className="btn-cta mt-8 cursor-pointer">{s.cta}</span>
              )
            )}
          </div>
        </div>
      ))}

      {/* Controls */}
      {n > 1 && (
        <>
          <button onClick={() => setI((i - 1 + n) % n)} className="flex absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-full bg-white/20 backdrop-blur text-white hover:bg-white/40 transition-colors" aria-label="Previous">
            <ChevronLeft strokeWidth={1.4} />
          </button>
          <button onClick={() => setI((i + 1) % n)} className="flex absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 items-center justify-center rounded-full bg-white/20 backdrop-blur text-white hover:bg-white/40 transition-colors" aria-label="Next">
            <ChevronRight strokeWidth={1.4} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx+1}`}
                className="flex items-center justify-center p-2.5 -m-2.5">
                <span className={`h-[2px] transition-all ${idx === i ? 'w-10 bg-white' : 'w-5 bg-white/50'}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroSlider;
